import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppDispatch, GetState, RootState } from '../../store'

import {
  ContactsTableFilters,
  ContactsTableFiltersOwner,
  AlternatePagination,
  CustomFieldSearch,
  FIELD_LIMIT,
  FIELD_INCLUDE_LIMIT,
  getUrl,
  getUrlBySortAndFilters,
  getFieldsKeyWords
} from './helpers'
import { fetchUsersByUrl } from '../../lib/api'
import { listenerMiddleware } from '../../store/listenerMiddleware'

export const OnwerFilterTypes = {
  I_OWN: 'Created by me',
  ALL: 'Anyone',
  FILTER_BY_USER: 'Created by user'
}

export type { ContactsTableFilters, CustomFieldSearch }
export {
  FIELD_LIMIT,
  FIELD_INCLUDE_LIMIT,
  getUrlBySortAndFilters,
  getFieldsKeyWords
}

export const ALL_COLUMNS = {
  name: 'Contacts',
  status_id: 'Status',
  phone: 'Phone',
  email: 'Email',
  tasks: 'Tasks',
  created_at: 'Created',
  updated_at: 'Updated',
  tags: 'Tags',
  type_id: 'Type'
}
export const CUSTOMER_TABLE_ORDER_SETTINGS_KEY = 'Contacts_table_order'
export const CUSTOMER_TABLE_VISIBILITY_SETTINGS_KEY =
  'Contacts_table_visibility'

//-name is desc
type ContactsTableState = {
  data: Array<any>
  filters: ContactsTableFilters
  sort: Array<'' | string>
  tableApiSort: Array<TableApiSort>
  pagination: AlternatePagination
  currentUser: null | string
  paginationTotal: number
  selected: Array<string>
  showSelector: boolean
  order: string[]
  visible: string[]
}

const initialPagination: AlternatePagination = {
  page: 0,
  per_page: 25
}

const initialFiltersState: ContactsTableFilters = {
  owner: { ownerId: 'null', ownerOption: OnwerFilterTypes.ALL },
  type: 'null',
  status: 'null'
}

const initialState: ContactsTableState = {
  data: [],
  filters: initialFiltersState,
  sort: [],
  tableApiSort: [],
  pagination: initialPagination,
  paginationTotal: 0,
  currentUser: null,
  selected: [],
  showSelector: false,
  order: [],
  visible: []
}

export const contactsTableSlice = createSlice({
  name: 'contactsTable',
  initialState,
  reducers: {
    setCurrentUser: (
      state: ContactsTableState,
      { payload }: PayloadAction<string>
    ) => {
      state.currentUser = payload
    },

    setPagination: (
      state: ContactsTableState,
      { payload }: PayloadAction<IndexPagination>
    ) => {
      state.pagination = payload
    },

    setPaginationPage: (
      state: ContactsTableState,
      { payload }: PayloadAction<number>
    ) => {
      state.pagination.page = payload
    },

    setPaginationTotal: (
      state: ContactsTableState,
      { payload }: PayloadAction<number>
    ) => {
      state.paginationTotal = payload
    },

    setPaginationSize: (
      state: ContactsTableState,
      { payload }: PayloadAction<number>
    ) => {
      state.pagination = { ...initialPagination, per_page: payload }
    },

    resetPagination: (state: ContactsTableState) => {
      state.pagination = initialPagination
    },

    setOwnerFilters: (
      state: ContactsTableState,
      { payload }: PayloadAction<ContactsTableFiltersOwner>
    ) => {
      state.filters.owner = payload
      state.selected = []
      state.showSelector = false
    },

    setStatusFilter: (
      state: ContactsTableState,
      { payload }: PayloadAction<string>
    ) => {
      state.filters.status = payload
      state.selected = []
      state.showSelector = false
    },

    setTypesFilter: (
      state: ContactsTableState,
      { payload }: PayloadAction<string>
    ) => {
      state.filters.type = payload
      state.selected = []
      state.showSelector = false
    },

    setOwnerId: (
      state: ContactsTableState,
      { payload }: PayloadAction<string>
    ) => {
      state.filters.owner.ownerId = payload
      state.selected = []
      state.showSelector = false
    },

    setSort: (
      state: ContactsTableState,
      {
        payload
      }: PayloadAction<{
        sort: Array<'' | string>
        apiTableSort: Array<TableApiSort>
      }>
    ) => {
      const { sort, apiTableSort } = payload
      state.sort = sort
      state.tableApiSort = apiTableSort
    },

    resetFilters: (state: ContactsTableState) => {
      state.filters = initialFiltersState
      state.selected = []
      state.showSelector = false
    },

    addSelected: (
      state: ContactsTableState,
      { payload }: PayloadAction<string>
    ) => {
      state.selected.push(payload)
      state.showSelector = true
    },

    removeSelected: (
      state: ContactsTableState,
      { payload }: PayloadAction<string>
    ) => {
      const newSelection = state.selected.filter(x => x !== payload)
      state.selected = newSelection
      state.showSelector = newSelection.length > 0
    },

    resetSelection: (state: ContactsTableState) => {
      state.selected = []
      state.showSelector = false
    },

    setOrder: (state, action: PayloadAction<string[]>) => {
      state.order = action.payload
    },

    setVisible: (state, action: PayloadAction<string[]>) => {
      state.visible = action.payload
    }
  }
})

export const {
  setPagination,
  setPaginationTotal,
  resetPagination,
  setPaginationPage,
  setPaginationSize,
  setOwnerFilters,
  setStatusFilter,
  setTypesFilter,
  setCurrentUser,
  resetFilters,
  setSort,
  setOwnerId,
  addSelected,
  removeSelected,
  resetSelection,
  setOrder,
  setVisible
} = contactsTableSlice.actions

export const fetchPageOfUsers = (
  filters: ContactsTableFilters,
  sort: Array<'' | string>,
  customFieldSearch: CustomFieldSearch = {
    customFields: [],
    fields: [],
    values: [],
    includedCustom: []
  },
  cb?: (data: APIUser[]) => void
) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    try {
      const { pagination } = selectContactsTable(getState())
      const result = await fetchUsersByUrl(
        getUrl(filters, sort, pagination, customFieldSearch)
      )
      dispatch(setPaginationTotal(result.pagination.total))
      cb && cb(result.data)
    } catch (err) {
      console.error('Error loading contacts', err)
      cb && cb([])
    }
  }
}

// Save customer table settings order to local storage on change
listenerMiddleware.startListening({
  actionCreator: setOrder,
  effect: (action, _) => {
    localStorage.setItem(
      CUSTOMER_TABLE_ORDER_SETTINGS_KEY,
      JSON.stringify(action.payload)
    )
  }
})

// Save customer table settings visibility to local storage on change
listenerMiddleware.startListening({
  actionCreator: setVisible,
  effect: (action, _) => {
    localStorage.setItem(
      CUSTOMER_TABLE_VISIBILITY_SETTINGS_KEY,
      JSON.stringify(action.payload)
    )
  }
})

export const selectContactsTable = (state: RootState) => state.contactsTable

export default contactsTableSlice.reducer
